import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format, parseISO } from 'date-fns';
import { enUS, it as itLocale } from 'date-fns/locale';
import { SEO } from 'src/components/SEO';
import { PublicMarketingFooter } from 'src/components/PublicMarketingFooter';
import { PublicScrollToTopFab } from 'src/components/PublicScrollToTopFab';
import { Logo } from 'src/components/Logo';
import {
  buildBlogPostPlainMarkdown,
  SITE_PUBLIC_ORIGIN,
} from 'src/data/blog/buildPlainMarkdownExport';
import { getBlogIndexPath, getBlogPostPath } from 'src/data/blog/paths';
import { getMetaAdsMcpFaqPageJsonLd } from 'src/data/blog/metaAdsMcpFaqLd';
import { getShopifyCohortAnalysisFaqPageJsonLd } from 'src/data/blog/shopifyCohortAnalysisFaqLd';
import {
  getAlternateBlogPost,
  getBlogPostBySlugAndLocale,
} from 'src/data/blog/selectors';
import type { BlogLocale } from 'src/data/blog/types';
import {
  extractMarkdownH2Toc,
  markdownNodeToPlainText,
  slugifyHeading,
} from './extractMarkdownToc';

const accent = '#1EED9F';
const muted = '#aab4c3';
const proseText = 'rgba(228, 236, 247, 0.92)';
const proseMuted = 'rgba(200, 212, 226, 0.88)';

/** Stili h2/h3 sui Box custom (react-markdown); scroll-margin per header fisso. */
const proseH2Sx = {
  color: '#f5f8fc',
  fontWeight: 800,
  fontSize: { xs: '1.28rem', md: '1.45rem' },
  letterSpacing: '-0.02em',
  lineHeight: 1.25,
  mt: { xs: 3.25, md: 4 },
  mb: 1.75,
  pb: 1.5,
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  scrollMarginTop: '5.5rem',
  '&:first-of-type': {
    mt: { xs: 0.5, md: 0.25 },
  },
} as const;

const proseH3Sx = {
  color: '#e8eef7',
  fontWeight: 800,
  fontSize: { xs: '1.05rem', md: '1.12rem' },
  letterSpacing: '-0.015em',
  mt: 2.75,
  mb: 1.1,
  scrollMarginTop: '5rem',
} as const;

/** Shell corpo articolo: misura di riga, vetro, ritmo verticale (H1/meta invariati fuori da qui). */
const articleBodyShellSx = {
  mt: { xs: 0.5, md: 1 },
  mx: 'auto',
  maxWidth: 'min(100%, 42rem)',
  px: { xs: 2.25, sm: 3.25, md: 4 },
  py: { xs: 3.25, md: 4.5 },
  borderRadius: { xs: 2.5, md: 3 },
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background:
    'linear-gradient(165deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.025) 45%, rgba(7, 12, 18, 0.55) 100%)',
  boxShadow:
    '0 4px 24px rgba(0,0,0,0.2), 0 32px 96px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
  backdropFilter: 'blur(14px)',
};

const articleProseSx = {
  ...articleBodyShellSx,
  '& p': {
    color: proseText,
    fontSize: { xs: '1.02rem', md: '1.0625rem' },
    lineHeight: 1.82,
    mb: '1.35em',
    letterSpacing: '0.01em',
    hyphens: 'none',
  },
  '& p:last-child': { mb: 0 },
  '& strong': {
    color: '#f2f6fb',
    fontWeight: 700,
  },
  '& hr': {
    border: 'none',
    height: 1,
    my: { xs: 3, md: 3.75 },
    background:
      'linear-gradient(90deg, transparent, rgba(30,237,159,0.35), transparent)',
    opacity: 0.9,
  },
  '& blockquote': {
    my: 2.75,
    mx: 0,
    pl: 2.25,
    pr: 2,
    py: 1.75,
    borderLeft: `4px solid ${accent}`,
    borderRadius: '0 12px 12px 0',
    background: 'rgba(30, 237, 159, 0.06)',
  },
  '& blockquote p': {
    color: proseMuted,
    fontSize: { xs: '0.98rem', md: '1.02rem' },
    lineHeight: 1.78,
    mb: '0.85em',
    fontStyle: 'italic',
  },
  '& blockquote p:last-child': { mb: 0 },
  '& ul, & ol': {
    pl: 2.25,
    mb: '1.65em',
    color: proseText,
  },
  '& ul': { listStyleType: 'disc' },
  '& ol': { listStyleType: 'decimal' },
  '& li': {
    mb: '0.65em',
    lineHeight: 1.78,
    paddingLeft: '0.35em',
  },
  '& li::marker': {
    color: 'rgba(30, 237, 159, 0.75)',
  },
  '& a': {
    color: accent,
    fontWeight: 600,
    textDecoration: 'underline',
    textDecorationColor: 'rgba(30, 237, 159, 0.35)',
    textUnderlineOffset: '3px',
    transition: 'color 0.15s ease, text-decoration-color 0.15s ease',
    '&:hover': {
      color: '#5fffc4',
      textDecorationColor: 'rgba(95, 255, 196, 0.55)',
    },
  },
  '& table': {
    width: '100%',
    borderCollapse: 'separate' as const,
    borderSpacing: 0,
    mb: '2em',
    fontSize: { xs: '0.8125rem', md: '0.875rem' },
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
  },
  '& thead th': {
    background: 'rgba(30, 237, 159, 0.12)',
    color: '#e8f7f0',
    fontWeight: 700,
    textAlign: 'left' as const,
    borderBottom: '1px solid rgba(255,255,255,0.12)',
  },
  '& tbody tr:nth-of-type(even) td': {
    background: 'rgba(255,255,255,0.02)',
  },
  '& th, & td': {
    borderColor: 'rgba(255,255,255,0.08)',
    borderStyle: 'solid',
    borderWidth: '0 0 1px 0',
    p: 1.25,
    verticalAlign: 'top' as const,
    lineHeight: 1.55,
  },
  '& tbody tr:last-child td': {
    borderBottom: 'none',
  },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.86em',
    background: 'rgba(0,0,0,0.35)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6,
    px: 0.75,
    py: 0.2,
    color: '#d4ecff',
  },
  /* Blocchi (indentati o fenced): il box e il wrap stanno su `pre`, non su `code` inline. */
  '& pre': {
    maxWidth: '100%',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    my: '1.25em',
    mx: 0,
    p: { xs: 1.25, md: 1.5 },
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.35)',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: { xs: '0.8125rem', md: '0.875rem' },
    lineHeight: 1.65,
    color: '#d4ecff',
    boxSizing: 'border-box',
  },
  '& pre code': {
    display: 'block',
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    px: 0,
    py: 0,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
};

const PageRoot = styled(Box)(() => ({
  minHeight: '100vh',
  width: '100%',
  color: '#fff',
  background:
    'radial-gradient(circle at 10% 0%, rgba(30, 237, 159, 0.18), transparent 26%), radial-gradient(circle at 100% 12%, rgba(1, 128, 148, 0.2), transparent 30%), linear-gradient(180deg, #071017 0%, #0b0e13 40%, #080c10 100%)',
  position: 'relative',
  overflowX: 'clip',
}));

function truncateBreadcrumbTitle(title: string, max = 52): string {
  const t = title.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

/** Numerazione sequenziale nel TOC (1-based), senza duplicare i "N." già tolti dal titolo markdown. */
function formatTocEntryLabel(index: number, label: string): string {
  return `${index + 1}. ${label}`;
}

export interface BlogPostPageProps {
  locale: BlogLocale;
}

export const BlogPostPage: React.FC<BlogPostPageProps> = ({ locale }) => {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const isMarkdownMirror = Boolean(slugParam && /\.md$/i.test(slugParam));
  const post = useMemo(
    () => getBlogPostBySlugAndLocale(slugParam, locale),
    [slugParam, locale],
  );

  const indexPath = getBlogIndexPath(locale);
  const dateLocale = locale === 'it' ? itLocale : enUS;

  useEffect(() => {
    const prev = document.documentElement.lang;
    document.documentElement.lang = locale === 'it' ? 'it' : 'en';
    return () => {
      document.documentElement.lang = prev;
    };
  }, [locale]);

  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
    noSsr: true,
  });
  const [readPct, setReadPct] = useState(0);

  const tocItems = useMemo(
    () =>
      post?.bodyMarkdown?.trim() && !isMarkdownMirror
        ? extractMarkdownH2Toc(post.bodyMarkdown)
        : [],
    [post?.bodyMarkdown, isMarkdownMirror],
  );

  const headingRenderRef = useRef({
    h2: 0,
    h3Counts: new Map<string, number>(),
  });

  const markdownComponents = useMemo(
    () => ({
      h2({ children }: { children?: React.ReactNode }) {
        const idx = headingRenderRef.current.h2++;
        const entry = tocItems[idx];
        const id = entry?.id ?? `h2-${idx}`;
        return (
          <Box component="h2" id={id} sx={proseH2Sx}>
            {children}
          </Box>
        );
      },
      h3({ children }: { children?: React.ReactNode }) {
        const text = markdownNodeToPlainText(children);
        const base = slugifyHeading(text) || 'subsection';
        const m = headingRenderRef.current.h3Counts;
        const n = m.get(base) ?? 0;
        m.set(base, n + 1);
        const id = n === 0 ? `h3-${base}` : `h3-${base}-${n + 1}`;
        return (
          <Box component="h3" id={id} sx={proseH3Sx}>
            {children}
          </Box>
        );
      },
    }),
    [tocItems],
  );

  useEffect(() => {
    if (!post?.bodyMarkdown?.trim() || isMarkdownMirror) return;
    const onScroll = () => {
      const el = document.documentElement;
      const h = el.scrollHeight - el.clientHeight;
      const st = window.scrollY;
      setReadPct(h > 0 ? Math.min(100, Math.round((st / h) * 1000) / 10) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [post?.bodyMarkdown, isMarkdownMirror, post?.slug]);

  if (!post) {
    return <Navigate to={indexPath} replace />;
  }

  const canonicalHtml = `${SITE_PUBLIC_ORIGIN}${getBlogPostPath(
    post.slug,
    locale,
  )}`;
  const markdownPublicUrl = `${canonicalHtml}.md`;
  const title = post.seoTitle || post.title;
  const description = post.seoDescription || post.excerpt;
  const ogImageUrl = post.cardImageSrc?.trim()
    ? `${SITE_PUBLIC_ORIGIN}${post.cardImageSrc}`
    : `${SITE_PUBLIC_ORIGIN}/images/hero-header-kipify.png`;
  const alternateLocale: BlogLocale = locale === 'it' ? 'en' : 'it';
  const alternatePost = getAlternateBlogPost(post, alternateLocale);
  const defaultHref =
    locale === 'en'
      ? canonicalHtml
      : alternatePost
        ? `${SITE_PUBLIC_ORIGIN}${getBlogPostPath(alternatePost.slug, 'en')}`
        : canonicalHtml;
  const alternates = isMarkdownMirror
    ? []
    : [
        { hrefLang: locale, href: canonicalHtml },
        ...(alternatePost
          ? [
              {
                hrefLang: alternateLocale,
                href: `${SITE_PUBLIC_ORIGIN}${getBlogPostPath(
                  alternatePost.slug,
                  alternateLocale,
                )}`,
              },
            ]
          : []),
        {
          hrefLang: 'x-default',
          href: defaultHref,
        },
      ];
  const indexable = Boolean(post.bodyMarkdown?.trim());
  const robots = isMarkdownMirror
    ? 'noindex, follow'
    : indexable
      ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      : 'noindex, follow';

  const structuredData = useMemo(() => {
    const baseGraph = [
      {
        '@type': 'Organization',
        '@id': `${SITE_PUBLIC_ORIGIN}/#organization`,
        name: 'Kipify',
        url: SITE_PUBLIC_ORIGIN,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_PUBLIC_ORIGIN,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `${SITE_PUBLIC_ORIGIN}${indexPath}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: post.title,
            item: canonicalHtml,
          },
        ],
      },
    ];

    if (isMarkdownMirror) {
      return { '@context': 'https://schema.org', '@graph': baseGraph };
    }

    if (!indexable) {
      return { '@context': 'https://schema.org', '@graph': baseGraph };
    }

    const graph: object[] = [
      ...baseGraph,
      {
        '@type': 'WebSite',
        '@id': `${SITE_PUBLIC_ORIGIN}/#website`,
        url: SITE_PUBLIC_ORIGIN,
        name: 'Kipify',
        publisher: { '@id': `${SITE_PUBLIC_ORIGIN}/#organization` },
      },
      {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.seoDescription || post.excerpt,
        url: canonicalHtml,
        datePublished: post.publishedAt,
        dateModified: post.updatedAt || post.publishedAt,
        inLanguage: locale === 'it' ? 'it-IT' : 'en-US',
        image: post.cardImageAlt
          ? [
              {
                '@type': 'ImageObject',
                url: ogImageUrl,
                caption: post.cardImageAlt,
              },
            ]
          : [ogImageUrl],
        author: post.author
          ? { '@type': 'Person', name: post.author }
          : { '@type': 'Organization', name: 'Kipify' },
        publisher: { '@id': `${SITE_PUBLIC_ORIGIN}/#organization` },
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalHtml },
      },
    ];

    if (post.translationKey === 'meta-ads-mcp-shopify') {
      graph.push(getMetaAdsMcpFaqPageJsonLd(locale));
    } else if (
      post.translationKey === 'shopify-cohort-repeat-purchase-products'
    ) {
      graph.push(getShopifyCohortAnalysisFaqPageJsonLd(locale));
    }

    return { '@context': 'https://schema.org', '@graph': graph };
  }, [
    canonicalHtml,
    indexPath,
    indexable,
    isMarkdownMirror,
    locale,
    post.author,
    post.bodyMarkdown,
    post.cardImageAlt,
    post.cardImageSrc,
    post.excerpt,
    post.seoDescription,
    ogImageUrl,
    post.publishedAt,
    post.slug,
    post.title,
    post.translationKey,
    post.updatedAt,
  ]);

  const plainMarkdown = useMemo(
    () => (indexable ? buildBlogPostPlainMarkdown(post) : ''),
    [indexable, post],
  );

  const mdFileName = `${post.slug}.md`;

  const mirrorIntro =
    locale === 'it'
      ? 'Qui sotto trovi il testo completo dell’articolo, senza menu né impaginazione. La versione ufficiale da indicizzare resta la pagina HTML.'
      : 'Below is the full article text without navigation or layout chrome. The official, indexable version is the HTML page.';

  const hasToc = tocItems.length > 0;
  const showReadingChrome = indexable && !isMarkdownMirror;

  const scrollToHeadingId = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  return (
    <PageRoot>
      {showReadingChrome ? (
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: 3,
            width: `${readPct}%`,
            bgcolor: accent,
            zIndex: 100002,
            pointerEvents: 'none',
            transition: reduceMotion ? 'none' : 'width 0.15s ease-out',
            boxShadow: '0 0 14px rgba(30,237,159,0.4)',
          }}
        />
      ) : null}
      <SEO
        title={`${title} | Kipify`}
        description={description}
        canonical={canonicalHtml}
        ogLocale={locale === 'it' ? 'it_IT' : 'en_US'}
        ogImage={ogImageUrl}
        ogType={indexable && !isMarkdownMirror ? 'article' : undefined}
        articlePublishedTime={
          indexable && !isMarkdownMirror
            ? `${post.publishedAt}T12:00:00.000Z`
            : undefined
        }
        articleModifiedTime={
          indexable && !isMarkdownMirror
            ? `${post.updatedAt || post.publishedAt}T12:00:00.000Z`
            : undefined
        }
        robots={robots}
        alternates={alternates}
        structuredData={structuredData}
        markdownAlternateHref={
          !isMarkdownMirror && indexable ? markdownPublicUrl : undefined
        }
      />

      <Box
        component="header"
        sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <Container maxWidth="md" sx={{ py: 2.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <MuiLink
              component={RouterLink}
              to="/"
              underline="none"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                color: '#fff',
                '&:focus-visible': {
                  outline: `2px solid ${accent}`,
                  outlineOffset: 4,
                },
              }}
              aria-label="Kipify home"
            >
              <Logo variant="default" size="small" />
              <Typography
                component="span"
                sx={{ fontWeight: 700, fontSize: '1.1rem' }}
              >
                Kipify
              </Typography>
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to={indexPath}
              underline="hover"
              sx={{
                color: muted,
                fontWeight: 600,
                fontSize: 14,
                '&:focus-visible': {
                  outline: `2px solid ${accent}`,
                  outlineOffset: 2,
                },
              }}
            >
              Blog
            </MuiLink>
          </Stack>
        </Container>
      </Box>

      <Box component="main" id="main-content">
        <Container
          maxWidth={hasToc ? 'lg' : 'md'}
          sx={{ py: { xs: 4, md: 6 }, pb: 10 }}
        >
          {isMarkdownMirror ? (
            <>
              <Typography
                variant="body2"
                sx={{ color: muted, mb: 2, lineHeight: 1.6 }}
              >
                {mirrorIntro}
              </Typography>
              <MuiLink
                component={RouterLink}
                to={getBlogPostPath(post.slug, locale)}
                sx={{ color: accent, fontWeight: 600, display: 'block', mb: 2 }}
              >
                {locale === 'it'
                  ? '← Apri versione HTML'
                  : '← Open HTML version'}
              </MuiLink>
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#dce4ef',
                  fontSize: 14,
                  lineHeight: 1.65,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid rgba(255,255,255,0.12)',
                  bgcolor: 'rgba(0,0,0,0.25)',
                }}
              >
                {plainMarkdown}
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="overline"
                sx={{ color: accent, letterSpacing: '0.12em', fontWeight: 700 }}
              >
                {post.category}
              </Typography>
              <Typography
                component="h1"
                variant="h3"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                  mt: 1,
                  mb: 2,
                  fontSize: { xs: '1.65rem', md: '2.25rem' },
                }}
              >
                {post.title}
              </Typography>
              <Stack spacing={1} sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: muted }}>
                  {format(parseISO(post.publishedAt), 'PP', {
                    locale: dateLocale,
                  })}
                  {post.readingTimeMinutes != null
                    ? ` · ${post.readingTimeMinutes} min`
                    : ''}
                </Typography>
                {indexable ? (
                  <MuiLink
                    component={RouterLink}
                    to={`${getBlogPostPath(post.slug, locale)}.md`}
                    underline="hover"
                    aria-label={
                      locale === 'it'
                        ? `Apri copia lineare dell’articolo (${mdFileName})`
                        : `Open linear article copy (${mdFileName})`
                    }
                    sx={{
                      color: accent,
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                      letterSpacing: '-0.02em',
                      wordBreak: 'break-all',
                    }}
                  >
                    {mdFileName}
                  </MuiLink>
                ) : null}
              </Stack>

              {!indexable ? (
                <Typography
                  variant="body1"
                  sx={{ color: '#dce4ef', lineHeight: 1.75 }}
                >
                  {post.excerpt}
                </Typography>
              ) : (
                <>
                  <Box
                    component="nav"
                    aria-label={
                      locale === 'it' ? 'Percorso di navigazione' : 'Breadcrumb'
                    }
                    sx={{
                      mb: 2,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 0.75,
                      rowGap: 0.5,
                    }}
                  >
                    <MuiLink
                      component={RouterLink}
                      to="/"
                      underline="hover"
                      sx={{
                        color: muted,
                        fontSize: 13,
                        fontWeight: 600,
                        '&:focus-visible': {
                          outline: `2px solid ${accent}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      Home
                    </MuiLink>
                    <Typography
                      component="span"
                      sx={{ color: muted, fontSize: 13 }}
                    >
                      /
                    </Typography>
                    <MuiLink
                      component={RouterLink}
                      to={indexPath}
                      underline="hover"
                      sx={{
                        color: muted,
                        fontSize: 13,
                        fontWeight: 600,
                        '&:focus-visible': {
                          outline: `2px solid ${accent}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      Blog
                    </MuiLink>
                    <Typography
                      component="span"
                      sx={{ color: muted, fontSize: 13 }}
                    >
                      /
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color: proseMuted,
                        fontSize: 13,
                        fontWeight: 500,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={post.title}
                    >
                      {truncateBreadcrumbTitle(post.title)}
                    </Typography>
                  </Box>
                  <Divider
                    sx={{
                      borderColor: 'rgba(255,255,255,0.08)',
                      mb: { xs: 2.5, md: 3.5 },
                      opacity: 1,
                    }}
                  />
                  {hasToc ? (
                    <Box
                      component="nav"
                      aria-label={
                        locale === 'it' ? 'Indice sezioni' : 'On this page'
                      }
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        mb: 2,
                        width: 1,
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        pb: 0.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.25}
                        sx={{ flexWrap: 'nowrap' }}
                      >
                        {tocItems.map((item, index) => (
                          <Box
                            key={item.id}
                            component="button"
                            type="button"
                            onClick={() => scrollToHeadingId(item.id)}
                            sx={{
                              flex: '0 0 auto',
                              cursor: 'pointer',
                              border: '1px solid rgba(255,255,255,0.12)',
                              borderRadius: 999,
                              px: 1.5,
                              py: 0.65,
                              fontSize: 12,
                              fontWeight: 600,
                              color: muted,
                              bgcolor: 'rgba(0,0,0,0.2)',
                              whiteSpace: 'nowrap',
                              textAlign: 'left',
                              fontFamily: 'inherit',
                              '&:hover': {
                                color: accent,
                                borderColor: 'rgba(30,237,159,0.35)',
                              },
                              '&:focus-visible': {
                                outline: `2px solid ${accent}`,
                                outlineOffset: 2,
                              },
                            }}
                          >
                            {formatTocEntryLabel(index, item.label)}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ md: 'flex-start' }}
                    spacing={{ xs: 0, md: 4 }}
                    sx={{ width: 1 }}
                  >
                    {hasToc ? (
                      <Box
                        component="nav"
                        aria-label={
                          locale === 'it'
                            ? 'Indice sezioni'
                            : 'Table of contents'
                        }
                        sx={{
                          display: { xs: 'none', md: 'block' },
                          width: 220,
                          flexShrink: 0,
                          position: 'sticky',
                          top: 88,
                          maxHeight: 'calc(100vh - 96px)',
                          overflowY: 'auto',
                          borderRadius: 2,
                          p: 2,
                          border: '1px solid rgba(255,255,255,0.08)',
                          bgcolor: 'rgba(0,0,0,0.22)',
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{
                            color: accent,
                            fontWeight: 700,
                            letterSpacing: '0.1em',
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          {locale === 'it' ? 'Indice' : 'Contents'}
                        </Typography>
                        <Stack
                          component="ul"
                          spacing={1}
                          sx={{
                            m: 0,
                            pl: 0,
                            listStyle: 'none',
                            color: muted,
                          }}
                        >
                          {tocItems.map((item, index) => (
                            <Box
                              component="li"
                              key={item.id}
                              sx={{
                                pl: 1.25,
                                borderLeft:
                                  '2px solid rgba(30, 237, 159, 0.25)',
                              }}
                            >
                              <Box
                                component="button"
                                type="button"
                                onClick={() => scrollToHeadingId(item.id)}
                                sx={{
                                  cursor: 'pointer',
                                  border: 'none',
                                  background: 'none',
                                  p: 0,
                                  m: 0,
                                  font: 'inherit',
                                  textAlign: 'left',
                                  color: muted,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  lineHeight: 1.45,
                                  textDecoration: 'underline',
                                  textDecorationColor: 'transparent',
                                  '&:hover': {
                                    color: accent,
                                    textDecorationColor:
                                      'rgba(30,237,159,0.35)',
                                  },
                                  '&:focus-visible': {
                                    outline: `2px solid ${accent}`,
                                    outlineOffset: 2,
                                    borderRadius: 0.5,
                                  },
                                }}
                              >
                                {formatTocEntryLabel(index, item.label)}
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box component="article" sx={articleProseSx}>
                        {(() => {
                          headingRenderRef.current.h2 = 0;
                          headingRenderRef.current.h3Counts.clear();
                          return (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents}
                            >
                              {post.bodyMarkdown!}
                            </ReactMarkdown>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Stack>
                </>
              )}
            </>
          )}
        </Container>
      </Box>

      {!isMarkdownMirror ? <PublicMarketingFooter /> : null}
      <PublicScrollToTopFab
        ariaLabel={locale === 'it' ? 'Torna su' : 'Back to top'}
      />
    </PageRoot>
  );
};
